// /admin/refills — clinician review of refill check-ins.
//
// Three tabs:
//   "Needs review" — status='submitted' (patient completed check-in)
//   "Overdue"      — status='pending' AND treatment.current_period_end
//                    has passed. The patient missed the window; we keep
//                    nudging them weekly, but the clinician may want to
//                    intervene (call, hold next shipment, etc).
//   "Decided"      — status in (approved, paused, cancelled), recent
//                    30 days — audit trail
//
// Clicking a row opens a detail panel with the patient's responses,
// prior refill history, weight history, treatment context. Action
// buttons (Approve / Pause / Cancel) post to admin-refill-decide
// with optional decision notes + optional message to patient that
// lands in the portal Messages thread.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { AdminLayout } from "@/components/AdminLayout";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { LoadingScreen } from "@/components/LoadingScreen";

type Decision = "approved" | "paused" | "cancelled";
type RefillStatus = "submitted" | "approved" | "pending" | "cancelled" | "paused" | "expired";

interface RefillRow {
  id: string;
  patient_id: string;
  treatment_id: string;
  status: string;
  responses: Record<string, unknown>;
  reminder_sent_at: string | null;
  opened_at: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  decision: string | null;
  decision_notes: string | null;
  created_at: string;
  patient: {
    first_name: string;
    last_name: string;
  } | null;
  treatment: {
    medication: string;
    dose: string | null;
    monthly_price: number | null;
    current_period_end: string | null;
  } | null;
}

type TabId = "needs_review" | "overdue" | "decided";

// "Overdue" is computed client-side: status='pending' AND
// treatment.current_period_end < now. We query the same pending set as
// the cron does and filter in JS.
const TABS: Array<{ id: TabId; label: string; statuses: RefillStatus[] }> = [
  { id: "needs_review", label: "Needs review", statuses: ["submitted"] },
  { id: "overdue",      label: "Overdue",      statuses: ["pending"] },
  { id: "decided",      label: "Decided",      statuses: ["approved", "paused", "cancelled"] },
];

export default function AdminRefills() {
  const [tab, setTab] = useState<TabId>("needs_review");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refillsQ = useQuery<RefillRow[]>({
    queryKey: ["admin-refills", tab],
    queryFn: async () => {
      const statuses = TABS.find((t) => t.id === tab)!.statuses;
      const { data, error } = await supabase
        .from("refill_requests")
        .select(`
          id, patient_id, treatment_id, status, responses, reminder_sent_at,
          opened_at, submitted_at, reviewed_at, decision, decision_notes, created_at,
          patient:patients(first_name, last_name),
          treatment:treatments(medication, dose, monthly_price, current_period_end)
        `)
        .in("status", statuses)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      const rows = (data as unknown as RefillRow[]) ?? [];
      // "Overdue" = pending whose treatment.current_period_end has passed.
      if (tab === "overdue") {
        const now = Date.now();
        return rows.filter((r) => {
          const pe = r.treatment?.current_period_end;
          return pe ? new Date(pe).getTime() < now : false;
        });
      }
      return rows;
    },
    refetchOnWindowFocus: true,
  });

  const counts = useTabCounts();

  const refills = refillsQ.data ?? [];
  const selected = refills.find((r) => r.id === selectedId) ?? null;

  return (
    <AdminLayout>
      <header className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h1 className="font-serif text-[26px] text-fh-ink">Refills</h1>
          <p className="text-[13px] text-fh-gray m-0">
            Review patient check-ins and decide whether to ship, pause, or cancel.
          </p>
        </div>
      </header>

      <div className="border-b border-fh-border mb-4 flex gap-1">
        {TABS.map((t) => {
          const count = counts.data?.[t.id] ?? 0;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); setSelectedId(null); }}
              className={[
                "px-3 py-2.5 text-[13.5px] font-semibold border-b-2 -mb-px transition flex items-center gap-2",
                active
                  ? "text-fh-green-dark border-fh-green"
                  : "text-fh-gray border-transparent hover:text-fh-ink",
              ].join(" ")}
            >
              {t.label}
              {count > 0 && (
                <span className={[
                  "inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-semibold leading-none",
                  active ? "bg-fh-green-dark text-white" : "bg-fh-sand text-fh-gray",
                ].join(" ")}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {refillsQ.isLoading ? (
        <LoadingScreen />
      ) : refills.length === 0 ? (
        <Card>
          <p className="text-[14px] text-fh-gray m-0 text-center py-8">
            No refill requests in this view.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
          <div className="flex flex-col gap-2">
            {refills.map((r) => (
              <RefillListItem
                key={r.id}
                refill={r}
                selected={r.id === selectedId}
                onClick={() => setSelectedId(r.id)}
              />
            ))}
          </div>
          <div>
            {selected ? (
              <RefillDetail refill={selected} key={selected.id} />
            ) : (
              <Card>
                <p className="text-[14px] text-fh-gray m-0 text-center py-8">
                  Pick a refill on the left to review.
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function useTabCounts() {
  return useQuery<Record<TabId, number>>({
    queryKey: ["admin-refills-counts"],
    queryFn: async () => {
      const counts: Record<TabId, number> = {
        needs_review: 0, overdue: 0, decided: 0,
      };
      const nowIso = new Date().toISOString();
      for (const t of TABS) {
        if (t.id === "overdue") {
          // count pending refill_requests whose linked treatment is
          // already past current_period_end. Postgrest doesn't support
          // joined-column filters in count queries, so we use the
          // inner-join form (!inner) + lt() on the embedded column.
          const { count } = await supabase
            .from("refill_requests")
            .select("id, treatment:treatments!inner(current_period_end)", { head: true, count: "exact" })
            .eq("status", "pending")
            .lt("treatment.current_period_end", nowIso);
          counts[t.id] = count ?? 0;
        } else {
          const { count } = await supabase
            .from("refill_requests")
            .select("id", { head: true, count: "exact" })
            .in("status", t.statuses);
          counts[t.id] = count ?? 0;
        }
      }
      return counts;
    },
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

function RefillListItem({
  refill, selected, onClick,
}: { refill: RefillRow; selected: boolean; onClick: () => void }) {
  const urgent = isUrgent(refill);
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "block w-full text-left p-3.5 rounded-[12px] border transition",
        selected
          ? "bg-white border-fh-green shadow-card"
          : "bg-white border-fh-border hover:border-fh-green-light",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-semibold text-fh-ink text-[14px]">
          {refill.patient?.first_name} {refill.patient?.last_name}
        </div>
        {urgent && (
          <span className="text-[11px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
            URGENT
          </span>
        )}
      </div>
      <div className="text-[13px] text-fh-gray mb-1">
        {refill.treatment?.medication ?? "—"}
        {refill.treatment?.dose && <> · {refill.treatment.dose}</>}
      </div>
      <div className="text-[12px] text-fh-gray-light">
        {refill.submitted_at
          ? `Submitted ${timeAgo(refill.submitted_at)}`
          : refill.reviewed_at
          ? `Decided ${timeAgo(refill.reviewed_at)}`
          : `Created ${timeAgo(refill.created_at)}`}
        {refill.treatment?.current_period_end && (
          <> · next charge {new Date(refill.treatment.current_period_end).toLocaleDateString()}</>
        )}
      </div>
    </button>
  );
}

function RefillDetail({ refill }: { refill: RefillRow }) {
  const qc = useQueryClient();
  const [decision, setDecision] = useState<Decision>("approved");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState("");
  const [saved, setSaved] = useState(false);

  const decide = useMutation<{ ok: true; message_id: string | null }, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<
        { ok?: boolean; message_id?: string; error?: string }
      >("admin-refill-decide", {
        body: {
          refill_request_id: refill.id,
          decision,
          decision_notes: notes.trim() || null,
          message_to_patient: message.trim() || null,
        },
      });
      if (error) throw new Error(error.message ?? "Decision failed");
      if (data?.error) throw new Error(data.error);
      return { ok: true, message_id: data?.message_id ?? null };
    },
    onSuccess: () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
      qc.invalidateQueries({ queryKey: ["admin-refills"] });
      qc.invalidateQueries({ queryKey: ["admin-refills-counts"] });
    },
  });

  const urgent = isUrgent(refill);

  return (
    <Card>
      {urgent && (
        <div className="mb-4">
          <Alert tone="warning">
            <strong>Red-flag answer detected.</strong> Patient reported a hospitalization,
            severe side effect, or thoughts of self-harm. Review carefully before approving.
          </Alert>
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[20px] font-serif text-fh-ink">
            {refill.patient?.first_name} {refill.patient?.last_name}
          </div>
          <div className="text-[13px] text-fh-gray">
            {refill.treatment?.medication ?? "—"}
            {refill.treatment?.dose && <> · {refill.treatment.dose}</>}
            {refill.treatment?.monthly_price !== null && refill.treatment?.monthly_price !== undefined && (
              <> · ${Number(refill.treatment.monthly_price).toFixed(2)}/cycle</>
            )}
          </div>
        </div>
        <RefillStatusBadge status={refill.status} />
      </div>

      {refill.decision && (
        <div className="mb-4 p-3 rounded-[8px] bg-fh-sand text-[13px] text-fh-ink">
          <strong>Prior decision:</strong> {refill.decision}
          {refill.decision_notes && <> — {refill.decision_notes}</>}
          {refill.reviewed_at && <> ({timeAgo(refill.reviewed_at)})</>}
        </div>
      )}

      <div className="mb-5">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-fh-gray mb-2">
          Patient responses
        </div>
        <ResponsesTable responses={refill.responses} />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 text-[12px]">
        <Meta label="Reminder sent"  value={dateLabel(refill.reminder_sent_at)} />
        <Meta label="Opened intake"  value={dateLabel(refill.opened_at)} />
        <Meta label="Submitted"      value={dateLabel(refill.submitted_at)} />
        <Meta label="Next charge"    value={dateLabel(refill.treatment?.current_period_end ?? null)} />
      </div>

      <hr className="border-t border-fh-border my-4" />

      <div className="text-[12px] font-semibold uppercase tracking-wider text-fh-gray mb-2">
        Decision
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {(["approved", "paused", "cancelled"] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDecision(d)}
            className={[
              "px-3 py-2 rounded-[8px] text-[13px] font-semibold border transition",
              decision === d
                ? d === "approved"  ? "bg-fh-green-dark text-white border-fh-green-dark"
                : d === "paused"    ? "bg-amber-600 text-white border-amber-600"
                : "bg-red-600 text-white border-red-600"
                : "bg-white text-fh-ink border-fh-border hover:border-fh-green",
            ].join(" ")}
          >
            {d === "approved" ? "Approve" : d === "paused" ? "Pause / needs attention" : "Decline"}
          </button>
        ))}
      </div>

      <div className="mb-3">
        <label className="text-[12px] font-semibold text-fh-gray block mb-1">
          Notes (internal, not shown to patient)
        </label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Patient reports nausea — adjusting to 0.5mg next cycle."
          className="w-full px-3 py-2 text-[13px] text-fh-ink border border-fh-border rounded-[8px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
        />
      </div>

      <div className="mb-4">
        <label className="text-[12px] font-semibold text-fh-gray block mb-1">
          Message to patient (optional — posted to their portal Messages)
        </label>
        <textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional. Patients see this in their portal. Email notification fires automatically."
          className="w-full px-3 py-2 text-[13px] text-fh-ink border border-fh-border rounded-[8px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green"
        />
      </div>

      {decide.error && (
        <div className="mb-3">
          <Alert tone="error">{decide.error.message}</Alert>
        </div>
      )}
      {saved && (
        <div className="mb-3">
          <Alert tone="success">Decision recorded.</Alert>
        </div>
      )}

      <Button loading={decide.isPending} onClick={() => decide.mutate()}>
        Record decision
      </Button>
    </Card>
  );
}

function ResponsesTable({ responses }: { responses: Record<string, unknown> }) {
  const entries = useMemo(() => Object.entries(responses ?? {}), [responses]);
  if (entries.length === 0) {
    return <div className="text-[13px] text-fh-gray italic">No responses recorded.</div>;
  }
  return (
    <ul className="divide-y divide-fh-border border border-fh-border rounded-[10px]">
      {entries.map(([k, v]) => (
        <li key={k} className="flex gap-3 px-3.5 py-2 text-[13px] items-start">
          <span className="text-fh-gray font-mono text-[12px] min-w-[140px]">{k}</span>
          <span className="text-fh-ink flex-1 break-words">{formatValue(v)}</span>
        </li>
      ))}
    </ul>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) {
    return v.map((item) =>
      typeof item === "object" && item !== null
        ? Object.entries(item).map(([k, val]) => `${k}=${val}`).join(", ")
        : String(item),
    ).join(" · ");
  }
  return JSON.stringify(v);
}

function RefillStatusBadge({ status }: { status: string }) {
  const tone = (() => {
    switch (status) {
      case "submitted": return "bg-fh-sage text-fh-green-dark";
      case "approved":  return "bg-emerald-100 text-emerald-700";
      case "paused":    return "bg-amber-100 text-amber-700";
      case "cancelled": return "bg-red-100 text-red-700";
      case "expired":   return "bg-fh-sand text-fh-gray";
      default:          return "bg-fh-sand text-fh-gray";
    }
  })();
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${tone}`}>
      {status}
    </span>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-fh-gray uppercase tracking-wider text-[10.5px] font-semibold">{label}</div>
      <div className="text-fh-ink font-semibold">{value}</div>
    </div>
  );
}

function dateLabel(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 14) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function isUrgent(refill: RefillRow): boolean {
  const r = refill.responses ?? {};
  if (r.hospitalizations === true) return true;
  if (r.thoughts_of_self_harm === true) return true;
  const sideEffects = (r.side_effects ?? []) as Array<{ name?: string; severity?: string }>;
  const urgentNames = new Set([
    "chest_pain", "severe_abdominal_pain", "vision_changes",
    "thoughts_of_self_harm", "severe_allergic_reaction", "fainting", "blood_in_stool",
  ]);
  for (const se of sideEffects) {
    if (!se?.name) continue;
    if (urgentNames.has(se.name)) return true;
    if (se.severity === "severe") return true;
  }
  return false;
}
